/**
 * CompetitorAnalysis 工具函数
 */

import type { PriceRangeStats, GlobalStats, KeywordStat, TreeNode, ProductDetail } from './types';
import { PRICE_RANGE_ORDER } from './constants';
import { ANALYSIS_PROMPTS } from '../../../PromptManagement/constants/analysisPrompts';

/**
 * 聚合多个价格区间数据
 */
export const aggregatePriceRanges = (
    priceRangesArray: { [range: string]: PriceRangeStats }[]
): { [range: string]: PriceRangeStats } => {
    const result: {
        [range: string]: {
            count: number;
            ratings: number[];
            days: number[];
            prices: number[];
            brands: number;
            sales: number;
            revenue: number;
            productsMap: { [asin: string]: ProductDetail };
        };
    } = {};

    priceRangesArray.forEach(pr => {
        if (!pr) return;
        Object.entries(pr).forEach(([range, data]) => {
            if (!result[range]) {
                result[range] = { count: 0, ratings: [], days: [], prices: [], brands: 0, sales: 0, revenue: 0, productsMap: {} };
            }
            result[range].count += data.count;
            if (data.avg_rating) result[range].ratings.push(data.avg_rating);
            if (data.avg_days) result[range].days.push(data.avg_days);
            if (data.avg_price) result[range].prices.push(data.avg_price);
            result[range].brands += data.brand_count;
            result[range].sales += data.total_sales || 0;
            result[range].revenue += data.total_revenue || 0;

            // Merge products
            if (data.products) {
                data.products.forEach(p => {
                    // If duplicate, keep the one with higher sales
                    if (!result[range].productsMap[p.asin] || (p.sales || 0) > (result[range].productsMap[p.asin].sales || 0)) {
                        result[range].productsMap[p.asin] = p;
                    }
                });
            }
        });
    });

    const totalCount = Object.values(result).reduce((s, d) => s + d.count, 0);
    const totalSales = Object.values(result).reduce((s, d) => s + d.sales, 0);
    const totalRevenue = Object.values(result).reduce((s, d) => s + d.revenue, 0);

    const final: { [range: string]: PriceRangeStats } = {};
    Object.entries(result).forEach(([range, d]) => {
        final[range] = {
            count: d.count,
            count_pct: totalCount ? Math.round((d.count / totalCount) * 1000) / 10 : 0,
            avg_rating: d.ratings.length
                ? Math.round((d.ratings.reduce((a, b) => a + b, 0) / d.ratings.length) * 100) / 100
                : null,
            avg_days: d.days.length ? Math.round(d.days.reduce((a, b) => a + b, 0) / d.days.length) : null,
            avg_price: d.prices.length
                ? Math.round((d.prices.reduce((a, b) => a + b, 0) / d.prices.length) * 100) / 100
                : null,
            brand_count: Math.round(d.brands / priceRangesArray.filter(p => p && p[range]).length) || 0,
            total_sales: d.sales || null,
            sales_pct: totalSales ? Math.round((d.sales / totalSales) * 1000) / 10 : 0,
            total_revenue: d.revenue || null,
            revenue_pct: totalRevenue ? Math.round((d.revenue / totalRevenue) * 1000) / 10 : 0,
            products: Object.values(d.productsMap).sort((a, b) => (b.sales || 0) - (a.sales || 0)),
        };
    });

    return final;
};

/**
 * 构建 AI 分析的 Prompt
 */
export const buildPrompt = (data: { keywords: KeywordStat[]; global_stats: GlobalStats }): string => {
    const template = ANALYSIS_PROMPTS.competitor_analysis || '';
    const gs = data.global_stats;

    // 构建价格区间分布文本
    const priceRangeDist = PRICE_RANGE_ORDER.filter(range => gs.price_ranges[range]?.count > 0)
        .map(range => {
            const pr = gs.price_ranges[range];
            return `- $${range}: 产品数${pr.count}(${pr.count_pct}%), 销量${pr.total_sales?.toLocaleString() || 0}(${pr.sales_pct}%), 销售额$${pr.total_revenue?.toLocaleString() || 0}(${pr.revenue_pct}%), 均价$${pr.avg_price?.toFixed(2) || '-'}, 评分${pr.avg_rating?.toFixed(2) || '-'}`;
        })
        .join('\n');

    // 构建关键词详情文本
    const keywordsDetail = data.keywords
        .map(
            kw =>
                `- **${kw.keyword}**: 结果数${kw.total_results}, 销量${kw.total_sales?.toLocaleString() || '-'}, 销售额$${kw.total_revenue?.toLocaleString() || '-'}, 均价$${kw.avg_price?.toFixed(2) || '-'}, 评分${kw.avg_rating?.toFixed(2) || '-'}, 品牌数${kw.brand_count}`
        )
        .join('\n');

    return template
        .replace('{{global_stats.unique_asins}}', String(gs.unique_asins))
        .replace('{{global_stats.unique_brands}}', String(gs.unique_brands))
        .replace('{{global_stats.total_sales}}', gs.total_sales?.toLocaleString() || '0')
        .replace('{{global_stats.total_revenue}}', gs.total_revenue?.toLocaleString() || '0')
        .replace('{{global_stats.avg_price}}', gs.avg_price?.toFixed(2) || '0')
        .replace('{{global_stats.avg_rating}}', gs.avg_rating?.toFixed(2) || '0')
        .replace('{{global_stats.avg_days}}', String(Math.round(gs.avg_days || 0)))
        .replace('{{price_range_distribution}}', priceRangeDist)
        .replace('{{keywords_detail}}', keywordsDetail);
};

/**
 * 构建价格区间树节点
 */
export const buildPriceRangeNodes = (
    priceRanges: { [range: string]: PriceRangeStats },
    keyPrefix: string
): TreeNode[] => {
    return PRICE_RANGE_ORDER.filter(range => priceRanges[range]?.count > 0).map(range => {
        const pr = priceRanges[range];

        let children: TreeNode[] | undefined;
        if (pr.products && pr.products.length > 0) {
            children = pr.products.map(p => ({
                key: `${keyPrefix}_${range}_${p.asin}`,
                name: p.title || p.asin,
                nodeType: 'product' as const,
                avg_rating: p.rating,
                avg_days: null, // N/A for single product usually
                avg_price: p.price,
                brand_count: 0, // N/A
                total_sales: p.sales,
                total_revenue: p.revenue,
                result_count: 0,
                // store plain data for render
                asin: p.asin,
                image_url: p.image_url,
            }));
        }

        return {
            key: `${keyPrefix}_${range}`,
            name: `$${range}`,
            nodeType: 'priceRange' as const,
            avg_rating: pr.avg_rating,
            avg_days: pr.avg_days,
            avg_price: pr.avg_price,
            brand_count: pr.brand_count,
            total_sales: pr.total_sales,
            sales_pct: pr.sales_pct,
            total_revenue: pr.total_revenue,
            revenue_pct: pr.revenue_pct,
            result_count: pr.count,
            count_pct: pr.count_pct,
            children: children,
        };
    });
};
